import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Study, SegmentType, UserProfile, Organization, CTAConfig, CTATypeConfig } from '../types';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import { AudioPlayer, AudioPlayerRef } from './AudioPlayer';
import { QuestionSegment } from './QuestionSegment';
import { CTAInterstitial } from './cta/CTAInterstitial';
import { completeStudy, saveStudyProgress, trackStudyView, saveReadingPreferences, getUserProfile } from '../services/firebase';
import { useAuthModal } from '../contexts/AuthContext';
import { generateStudyPDF } from '../services/pdfService';

interface StudyViewerProps {
  study: Study;
  userId?: string | null;
  user?: UserProfile | null;
  organization?: Organization | null;
  onExit: () => void;
  onComplete?: (awards: any[]) => void;
  onLogout?: () => void;
  onStartNextStudy?: (nextStudyId: string) => void;
  onCourseClick?: () => void;
  initialProgress?: number;
  isPartOfCourse?: boolean;
  courseContext?: {
    courseId: string;
    courseTitle: string;
    isPublic: boolean;
    nextStudy?: {
      id: string;
      title: string;
      coverImage?: string;
    } | null;
  };
}

const getDynamicTextClass = (text: string, sizePref: 'small' | 'medium' | 'large') => {
  const length = text.length;

  // Very short text (e.g. quotes, emphasis)
  if (length < 150) {
    switch (sizePref) {
      case 'small': return 'text-lg md:text-2xl leading-normal';
      case 'large': return 'text-2xl md:text-4xl leading-tight';
      default: return 'text-xl md:text-3xl leading-snug';
    }
  }

  // Short paragraph
  if (length < 400) {
    switch (sizePref) {
      case 'small': return 'text-base md:text-lg leading-relaxed';
      case 'large': return 'text-xl md:text-2xl leading-relaxed';
      default: return 'text-lg md:text-xl leading-relaxed';
    }
  }

  // Medium paragraph
  if (length < 800) {
    switch (sizePref) {
      case 'small': return 'text-sm md:text-base leading-relaxed';
      case 'large': return 'text-lg md:text-xl leading-relaxed';
      default: return 'text-base md:text-lg leading-relaxed';
    }
  }

  // Long text
  switch (sizePref) {
    case 'small': return 'text-sm leading-relaxed';
    case 'large': return 'text-lg md:text-xl leading-relaxed';
    default: return 'text-base md:text-lg leading-relaxed';
  }
};

export const StudyViewer: React.FC<StudyViewerProps> = ({ study, userId, user, organization, onExit, onComplete, onLogout, onStartNextStudy, onCourseClick, initialProgress = 0, isPartOfCourse = false, courseContext }) => {
  const { openAuthModal } = useAuthModal();
  const [activeTab, setActiveTab] = useState('browse');

  // Normalize legacy segment types for backward compatibility
  const normalizedStudy: Study = useMemo(() => {
    return {
      ...study,
      segments: study.segments.map((s: any) => {
        // Convert legacy 'youtube' to 'video'
        if (s.type === 'youtube') {
          return { ...s, type: 'video' as SegmentType };
        }
        return s;
      })
    };
  }, [study]);

  const [currentIndex, setCurrentIndex] = useState(initialProgress);
  const [isCompleted, setIsCompleted] = useState(false);
  const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState<number | null>(null);

  // Quiz specific state
  const [quizSelectedOption, setQuizSelectedOption] = useState<number | null>(null);
  const [quizIsCorrect, setQuizIsCorrect] = useState<boolean | null>(null);

  // Reading Mode state
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [theme, setTheme] = useState<'light' | 'sepia' | 'dark'>('light');
  const [showReadingOptions, setShowReadingOptions] = useState(false);

  // Auto-play state
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(false);
  const [isAudioPaused, setIsAudioPaused] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const audioPlayerRef = useRef<AudioPlayerRef | null>(null);

  // Touch/swipe state
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Video refs for auto-play and auto-advance
  const videoRef = useRef<HTMLVideoElement>(null);
  const youtubePlayerRef = useRef<any>(null);
  const youtubeContainerRef = useRef<HTMLDivElement>(null);

  const currentSegment = normalizedStudy.segments?.[currentIndex];
  const progress = ((currentIndex + 1) / (normalizedStudy.segments?.length || 1)) * 100;
  const isAuthenticated = !!userId;

  // Auto-advance countdown for course navigation
  useEffect(() => {
    if (isCompleted && courseContext?.nextStudy && onStartNextStudy) {
      setAutoAdvanceCountdown(15);

      const interval = setInterval(() => {
        setAutoAdvanceCountdown(prev => {
          if (prev === null || prev <= 1) {
            clearInterval(interval);
            if (prev === 1) {
              onStartNextStudy(courseContext.nextStudy!.id);
            }
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [isCompleted, courseContext?.nextStudy, onStartNextStudy]);

  // Load user preferences and notes on mount
  useEffect(() => {
    const loadUserData = async () => {
      // Skip loading user data if not authenticated
      if (!userId) return;

      try {
        const userProfile = await getUserProfile(userId);
        if (userProfile) {
          // Load reading preferences
          if (userProfile.readingPreferences) {
            setFontSize(userProfile.readingPreferences.fontSize);
            setTheme(userProfile.readingPreferences.theme);
          }
        }

        // Track study view
        await trackStudyView(study.id, userId);
      } catch (error) {
        console.error('Failed to load user data:', error);
      }
    };

    loadUserData();
  }, [study.id, userId]);

  // Reset quiz state when segment changes
  useEffect(() => {
    setQuizSelectedOption(null);
    setQuizIsCorrect(null);
  }, [currentIndex]);

  // Keyboard navigation (left/right arrow keys)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, normalizedStudy.segments.length, isCompleted]);

  // Save reading preferences when they change
  useEffect(() => {
    const savePrefs = async () => {
      if (!userId) return;
      try {
        await saveReadingPreferences(userId, { fontSize, theme });
      } catch (error) {
        console.error('Failed to save preferences:', error);
      }
    };
    savePrefs();
  }, [fontSize, theme, userId]);

  // Save progress whenever index changes (only if authenticated)
  useEffect(() => {
    if (currentIndex > 0 && !isCompleted && userId) {
      saveStudyProgress(userId, study.id, currentIndex);
    }
  }, [currentIndex, isCompleted, study.id, userId]);

  // Safety check - if no valid segment, reset to beginning
  useEffect(() => {
    if (!currentSegment && normalizedStudy.segments?.length > 0) {
      console.warn('No valid segment found at index', currentIndex, '- resetting to beginning');
      setCurrentIndex(0);
    }
  }, [currentSegment, currentIndex, normalizedStudy.segments]);

  // Auto-play and auto-advance for video segments
  useEffect(() => {
    if (currentSegment?.type === 'video') {
      // Handle hosted videos (uploaded/AI-generated)
      if (currentSegment.metadata?.videoType === 'hosted' && videoRef.current) {
        const video = videoRef.current;

        // Auto-play the video
        const playVideo = async () => {
          try {
            await video.play();
          } catch (error) {
            console.log('Auto-play prevented by browser:', error);
          }
        };

        playVideo();

        // Auto-advance when video ends
        const handleVideoEnd = () => {
          handleNext();
        };

        video.addEventListener('ended', handleVideoEnd);

        return () => {
          video.removeEventListener('ended', handleVideoEnd);
        };
      }
      // Handle YouTube videos
      else if (currentSegment.metadata?.videoType === 'youtube' || !currentSegment.metadata?.videoType) {
        // Load YouTube IFrame API if not already loaded
        if (!(window as any).YT) {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        }

        // Wait for API to be ready and create player
        const initPlayer = () => {
          if (youtubeContainerRef.current && (window as any).YT) {
            const videoId = currentSegment.content.split('v=')[1]?.split('&')[0] || currentSegment.content.split('/').pop();

            // Destroy existing player if any
            if (youtubePlayerRef.current) {
              youtubePlayerRef.current.destroy();
            }

            youtubePlayerRef.current = new (window as any).YT.Player(youtubeContainerRef.current, {
              videoId: videoId,
              playerVars: {
                autoplay: 1,
                modestbranding: 1,
              },
              events: {
                onStateChange: (event: any) => {
                  // YT.PlayerState.ENDED = 0
                  if (event.data === 0) {
                    handleNext();
                  }
                }
              }
            });
          }
        };

        if ((window as any).YT && (window as any).YT.Player) {
          initPlayer();
        } else {
          (window as any).onYouTubeIframeAPIReady = initPlayer;
        }

        return () => {
          if (youtubePlayerRef.current) {
            youtubePlayerRef.current.destroy();
            youtubePlayerRef.current = null;
          }
        };
      }
    }
  }, [currentIndex, currentSegment]);

  // Touch/Swipe gesture handlers
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;

      // Only trigger swipe if horizontal movement is greater than vertical (to avoid interfering with scrolling)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          // Swipe right - go to previous
          handlePrev();
        } else {
          // Swipe left - go to next
          handleNext();
        }
      }

      touchStartX.current = null;
      touchStartY.current = null;
    };

    contentElement.addEventListener('touchstart', handleTouchStart);
    contentElement.addEventListener('touchend', handleTouchEnd);

    return () => {
      contentElement.removeEventListener('touchstart', handleTouchStart);
      contentElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentIndex, normalizedStudy.segments.length, isCompleted]);

  // Early return if still no segment after reset attempt
  if (!currentSegment) {
    return null;
  }

  const handleNext = async () => {
    if (currentIndex < normalizedStudy.segments.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      // Only save progress and complete if authenticated
      if (userId) {
        await saveStudyProgress(userId, study.id, normalizedStudy.segments.length);
        const result = await completeStudy(userId, study.id);
        setIsCompleted(true);
        if (result && result.newAwards && onComplete) {
          onComplete(result.newAwards);
        } else if (onComplete) {
          onComplete([]);
        }
      } else {
        setIsCompleted(true);
      }
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  const handleQuizOptionClick = (index: number) => {
    setQuizSelectedOption(index);
    const correctIndex = currentSegment.metadata?.correctAnswer ?? -1;
    setQuizIsCorrect(index === correctIndex);
  };

  const handleAudioPlaybackEnd = () => {
    // When audio ends and auto-play is enabled, advance to next slide
    if (isAutoPlayEnabled) {
      // Check if next segment is readable (text, verse, or quiz)
      const nextIndex = currentIndex + 1;
      if (nextIndex < normalizedStudy.segments.length) {
        const nextSegment = normalizedStudy.segments[nextIndex];
        // If next segment is image or CTA, skip auto-advance and disable auto-play
        // Videos will auto-play and auto-advance on their own
        // CTAs require user interaction, so don't auto-advance
        if (nextSegment.type === 'image' || nextSegment.type === 'cta') {
          setIsAutoPlayEnabled(false);
        } else {
          // Auto-advance to next slide
          handleNext();
        }
      } else {
        // Reached the end of the study
        handleNext();
      }
    }
  };

  const handleAutoPlayButtonClick = () => {
    // If paused, resume playback
    if (isAudioPaused && audioPlayerRef.current) {
      audioPlayerRef.current.resumePlayback();
    } else if (isAutoPlayEnabled && audioPlayerRef.current) {
      // If playing, pause it
      audioPlayerRef.current.pausePlayback();
    } else {
      // If not playing, start playing
      setIsAutoPlayEnabled(true);
    }
  };

  const handleDownloadPDF = async () => {
    if (isGeneratingPDF) return;
    setIsGeneratingPDF(true);
    try {
      await generateStudyPDF(normalizedStudy);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (isCompleted) {
    const nextStudy = courseContext?.nextStudy;

    return (
      <div className="min-h-screen bg-primary flex flex-col text-white">
        {/* Header with Logo - only show for authenticated users (unauthenticated users see it in the banner) */}
        {isAuthenticated && (
          <div className="w-full px-6 py-4">
            <div className="max-w-7xl mx-auto flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <Icons.BookOpen className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-serif font-bold text-xl text-white tracking-tight leading-none">Bible Aware</span>
                {organization && <span className="text-xs text-white/70 font-medium">{organization.name}</span>}
              </div>
            </div>
          </div>
        )}

        {/* Completion Content */}
        <div className="flex-1 flex items-center justify-center p-6 text-center">
          <div className="max-w-md w-full animate-fade-in-up">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/50">
                <Icons.Check className="w-5 h-5 text-blue-900" />
              </div>
              <h2 className="text-3xl font-serif font-bold">Study Complete!</h2>
            </div>
            <p className="text-blue-100 mb-6 text-lg">You have successfully finished "{study.title}".</p>

            {/* Next Study Card */}
            {nextStudy && onStartNextStudy && (
              <div className="bg-white rounded-xl p-5 mb-6 shadow-lg">
                <div className="flex items-center gap-2 mb-4">
                  <p className="text-gray-500 text-sm uppercase tracking-wide">Up Next</p>
                  {autoAdvanceCountdown !== null && (
                    <span className="bg-primary text-white text-sm font-semibold px-3 py-1 rounded-full animate-pulse">
                      in {autoAdvanceCountdown}s
                    </span>
                  )}
                </div>
                {nextStudy.coverImage ? (
                  <img
                    src={nextStudy.coverImage}
                    alt={nextStudy.title}
                    className="w-full h-40 rounded-lg object-cover mb-4"
                  />
                ) : (
                  <div className="w-full h-40 rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                    <Icons.BookOpen className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <h3 className="font-semibold text-gray-800 text-xl leading-tight mb-4">{nextStudy.title}</h3>
                <button
                  onClick={() => onStartNextStudy(nextStudy.id)}
                  className="w-full py-3 bg-secondary text-primary font-bold rounded-lg hover:bg-yellow-300 transition-colors shadow-lg flex items-center justify-center gap-2"
                >
                  Continue Now
                  <Icons.ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}

            <button
              onClick={() => {
                setAutoAdvanceCountdown(null);
                onExit();
              }}
              className={`w-full py-3 font-bold rounded-lg transition-colors shadow-lg ${nextStudy ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-primary hover:bg-gray-100'
                }`}
            >
              {isPartOfCourse ? 'Return to Course' : 'Return to Dashboard'}
            </button>
            {!isAuthenticated && (
              <button
                onClick={() => openAuthModal('signup')}
                className="w-full py-3 mt-4 bg-secondary text-primary font-bold rounded-lg hover:bg-yellow-300 transition-colors shadow-lg"
              >
                Create Free Account to Save Progress
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header with Navigation */}
      {user ? (
        <AppHeader
          user={user}
          organization={organization || null}
          onLogout={onLogout || (() => { })}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      ) : null}

      {/* Study Info Bar */}
      <div className="bg-white shadow-sm sticky top-[73px] z-20 border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3">
          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Top Row: Exit button, Title, and Play button */}
            <div className="flex items-center gap-3 mb-3">
              <button onClick={onExit} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Exit Study">
                <Icons.Logout className="w-5 h-5" />
              </button>
              <div className="flex-1 min-w-0">
                {courseContext && (
                  <button
                    onClick={onCourseClick}
                    className="text-xs text-blue-600 truncate hover:underline text-left"
                  >
                    {courseContext.courseTitle}
                  </button>
                )}
                <h3 className="font-semibold text-gray-800 line-clamp-1 text-sm">{study.title}</h3>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                  <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="whitespace-nowrap">{currentIndex + 1} of {normalizedStudy.segments.length}</span>
                </div>
              </div>
              {/* Audio Play/Pause Button */}
              <button
                onClick={handleAutoPlayButtonClick}
                disabled={!currentSegment.audioUrl && (!currentSegment.content || currentSegment.type === 'image' || currentSegment.type === 'video' || currentSegment.type === 'cta')}
                className={`px-3 py-1.5 rounded-lg transition-all text-sm font-semibold flex items-center gap-1 flex-shrink-0 ${!currentSegment.audioUrl && (!currentSegment.content || currentSegment.type === 'image' || currentSegment.type === 'video' || currentSegment.type === 'cta')
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : isAutoPlayEnabled
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                title={
                  !currentSegment.audioUrl && (!currentSegment.content || currentSegment.type === 'image' || currentSegment.type === 'video' || currentSegment.type === 'cta')
                    ? 'No audio available'
                    : isAutoPlayEnabled
                      ? (isAudioPaused ? 'Resume audio' : 'Pause audio')
                      : 'Play audio'
                }
              >
                {isAutoPlayEnabled && !isAudioPaused ? (
                  <Icons.Pause className="w-4 h-4" />
                ) : (
                  <Icons.Play className="w-4 h-4" />
                )}
                <span className="text-xs">
                  {!currentSegment.audioUrl && (!currentSegment.content || currentSegment.type === 'image' || currentSegment.type === 'video' || currentSegment.type === 'cta')
                    ? 'Play'
                    : isAutoPlayEnabled
                      ? (isAudioPaused ? 'Play' : 'Pause')
                      : 'Play'}
                </span>
              </button>

            </div>

            {/* Navigation Buttons Row */}
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={handlePrev}
                disabled={currentIndex === 0}
                className="flex items-center justify-center gap-1 px-3 py-2 text-gray-600 text-sm font-medium disabled:opacity-30 hover:bg-gray-50 rounded-lg transition-colors flex-1"
              >
                <Icons.Prev className="w-4 h-4" />
                <span className="hidden xs:inline">Prev</span>
              </button>

              <button
                onClick={handleNext}
                className="flex items-center justify-center gap-1 px-3 py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-blue-900 transition-all shadow-md flex-1"
              >
                <span>{currentIndex === normalizedStudy.segments.length - 1 ? 'Finish' : 'Next'}</span>
                {currentIndex !== normalizedStudy.segments.length - 1 && <Icons.Next className="w-4 h-4" />}
              </button>
            </div>

            {/* Icon Controls Row */}
            <div className="flex items-center justify-end gap-2">
              {/* Icon Controls */}
              <div className="flex items-center gap-1">
              </div>
            </div>

          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex-1 flex items-center gap-6">
              <button onClick={onExit} className="text-gray-400 hover:text-gray-600" title="Exit Study">
                <Icons.Logout className="w-5 h-5" />
              </button>
              <div>
                {courseContext && (
                  <button
                    onClick={onCourseClick}
                    className="text-xs text-blue-600 hover:underline text-left"
                  >
                    {courseContext.courseTitle}
                  </button>
                )}
                <h3 className="font-semibold text-gray-800 line-clamp-1 text-sm">{study.title}</h3>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                  <div className="w-32 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="whitespace-nowrap">{currentIndex + 1} of {normalizedStudy.segments.length}</span>
                </div>
              </div>

              {/* Inline Audio Player */}
              <div className="ml-8">
                <h3 className="font-semibold text-gray-800 text-sm">Play Audio</h3>
                <div className="mt-2 flex items-center gap-3">
                  {/* Play/Pause Button */}
                  <button
                    onClick={handleAutoPlayButtonClick}
                    disabled={!currentSegment.audioUrl && (!currentSegment.content || currentSegment.type === 'image' || currentSegment.type === 'video' || currentSegment.type === 'cta')}
                    className={`px-6 py-2.5 rounded-lg transition-all font-semibold shadow-md hover:shadow-lg ${!currentSegment.audioUrl && (!currentSegment.content || currentSegment.type === 'image' || currentSegment.type === 'video' || currentSegment.type === 'cta')
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : isAutoPlayEnabled
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    title={
                      !currentSegment.audioUrl && (!currentSegment.content || currentSegment.type === 'image' || currentSegment.type === 'video' || currentSegment.type === 'cta')
                        ? 'No audio available'
                        : isAutoPlayEnabled
                          ? (isAudioPaused ? 'Resume audio' : 'Pause audio')
                          : 'Play audio'
                    }
                  >
                    <div className="flex items-center gap-2">
                      {isAutoPlayEnabled && !isAudioPaused ? (
                        <Icons.Pause className="w-5 h-5" />
                      ) : (
                        <Icons.Play className="w-5 h-5" />
                      )}
                      <span>
                        {!currentSegment.audioUrl && (!currentSegment.content || currentSegment.type === 'image' || currentSegment.type === 'video' || currentSegment.type === 'cta')
                          ? 'Play'
                          : isAutoPlayEnabled
                            ? (isAudioPaused ? 'Play' : 'Pause')
                            : 'Play'}
                      </span>
                    </div>
                  </button>

                  {/* Audio Player - Always rendered, controls only visible on desktop when playing */}
                  {(currentSegment.audioUrl || currentSegment.content) && (
                    <AudioPlayer
                      ref={audioPlayerRef}
                      text={currentSegment.content}
                      segmentType={currentSegment.type}
                      audioUrl={currentSegment.audioUrl}
                      autoPlay={isAutoPlayEnabled}
                      onPlaybackEnd={handleAudioPlaybackEnd}
                      onPauseChange={setIsAudioPaused}
                      className="hidden md:flex"
                    />
                  )}
                </div>
              </div>

              {/* PDF Download Button (Desktop) */}
              <div className="ml-4">
                <h3 className="font-semibold text-gray-800 text-sm">Offline Version</h3>
                <button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className={`mt-2 px-4 py-1.5 rounded-lg transition-all text-sm font-semibold shadow-sm hover:shadow-md flex items-center gap-2 ${isGeneratingPDF ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                    }`}
                >
                  {isGeneratingPDF ? (
                    <Icons.Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Icons.Download className="w-4 h-4 text-primary" />
                  )}
                  <span>{isGeneratingPDF ? 'Generating...' : 'Download PDF'}</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={handlePrev}
                disabled={currentIndex === 0}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 font-medium disabled:opacity-30 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <Icons.Prev className="w-5 h-5" />
                <span>Previous</span>
              </button>

              {/* Next Button */}
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-bold rounded-lg hover:bg-blue-900 transition-all shadow-md hover:shadow-lg"
              >
                <span>{currentIndex === normalizedStudy.segments.length - 1 ? 'Finish' : 'Next'}</span>
                {currentIndex !== normalizedStudy.segments.length - 1 && <Icons.Next className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full p-4 md:p-6 pt-2 md:pt-8 justify-start md:justify-center">
        <div
          ref={contentRef}
          className={`rounded-2xl shadow-xl p-4 md:p-12 min-h-[200px] md:min-h-[500px] flex flex-col items-center justify-center text-center animate-fade-in relative overflow-auto transition-colors ${theme === 'light' ? 'bg-white text-gray-900' :
            theme === 'sepia' ? 'bg-amber-50 text-amber-950' :
              'bg-gray-900 text-gray-100'
            } ${fontSize === 'small' ? 'text-sm' :
              fontSize === 'large' ? 'text-lg' :
                'text-base'
            }`}>

          {/* Cover Image Background (if exists) */}
          {study.coverImage && (
            <div
              className="absolute inset-0 bg-cover bg-center opacity-10 bg-fixed"
              style={{
                backgroundImage: `url(${study.coverImage})`,
                backgroundPosition: `${study.coverImagePosition?.x || 50}% ${study.coverImagePosition?.y || 50}%`,
                backgroundAttachment: 'fixed'
              }}
            />
          )}

          {/* Segment Content Renderer */}
          {currentSegment.type === 'text' && (
            <div className="prose max-w-none w-full relative z-10 flex flex-col items-center">
              <p className={`whitespace-pre-wrap text-left w-full ${getDynamicTextClass(currentSegment.content, fontSize)}`}>
                {currentSegment.content}
              </p>
            </div>
          )}

          {currentSegment.type === 'verse' && (
            <div className="max-w-xl relative z-10">
              <p className="font-serif text-xl md:text-2xl text-primary italic mb-4">"{currentSegment.content}"</p>
              {currentSegment.metadata?.book && currentSegment.metadata?.chapter && currentSegment.metadata?.verses && (
                <p className="font-bold text-sm md:text-base">
                  — {currentSegment.metadata.book} {currentSegment.metadata.chapter}:{currentSegment.metadata.verses}{currentSegment.metadata.version && currentSegment.metadata.version !== 'Other' ? ` (${currentSegment.metadata.version})` : ''}
                </p>
              )}
            </div>
          )}

          {currentSegment.type === 'image' && (
            <div className="w-full relative z-10">
              <img
                src={currentSegment.content}
                alt={currentSegment.metadata?.prompt || 'Study Image'}
                className="rounded-lg shadow-lg max-h-[350px] md:max-h-[500px] w-auto mx-auto object-cover"
              />
              {currentSegment.metadata?.caption && (
                <p className="mt-4 text-gray-500 italic text-sm">{currentSegment.metadata.caption}</p>
              )}
            </div>
          )}

          {currentSegment.type === 'image-text' && (
            <div className={`w-full relative z-10 flex flex-col ${currentSegment.metadata?.imageUrl && currentSegment.content ? 'md:flex-row gap-6 md:gap-8 items-center text-left' : 'items-center'}`}>

              {/* Image Side - Only if image exists */}
              {currentSegment.metadata?.imageUrl && (
                <div className={`${currentSegment.content ? 'w-full md:w-1/2 flex-shrink-0' : 'w-full max-w-2xl mx-auto'}`}>
                  <img
                    src={currentSegment.metadata.imageUrl}
                    alt={currentSegment.metadata?.prompt || 'Study Image'}
                    className={`rounded-lg shadow-lg w-full h-auto object-cover ${currentSegment.content ? 'max-h-[400px]' : 'max-h-[500px]'} mx-auto`}
                  />
                  {currentSegment.metadata?.caption && (
                    <p className={`mt-2 text-gray-500 italic text-sm ${currentSegment.content ? 'text-center md:text-left' : 'text-center'}`}>
                      {currentSegment.metadata.caption}
                    </p>
                  )}
                </div>
              )}

              {/* Text Side - Only if text exists */}
              {currentSegment.content && (
                <div className={`${currentSegment.metadata?.imageUrl ? 'w-full md:w-1/2' : 'w-full flex flex-col items-center text-center'} prose max-w-none`}>
                  <p className={`whitespace-pre-wrap ${currentSegment.metadata?.imageUrl ? '' : 'w-full'} ${getDynamicTextClass(currentSegment.content, fontSize)}`}>
                    {currentSegment.content}
                  </p>
                </div>
              )}
            </div>
          )}

          {currentSegment.type === 'video' && (
            <div className="w-full aspect-video bg-black rounded-xl overflow-hidden shadow-lg relative z-10">
              {currentSegment.metadata?.videoType === 'hosted' ? (
                <video
                  ref={videoRef}
                  src={currentSegment.content}
                  className="w-full h-full object-contain"
                  controls
                  playsInline
                />
              ) : (
                <div ref={youtubeContainerRef} className="w-full h-full" />
              )}
            </div>
          )}

          {currentSegment.type === 'quiz' && (
            <div className="w-full max-w-md mx-auto relative z-10">
              <div className="mb-6 md:mb-8">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 text-primary">
                  <Icons.Quiz className="w-6 h-6 md:w-8 md:h-8" />
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-gray-900 mb-2">{currentSegment.content}</h3>
                <p className="text-gray-500 text-xs md:text-sm">Select the correct answer below</p>
              </div>

              <div className="space-y-2 md:space-y-3">
                {currentSegment.metadata?.quizOptions?.map((option: string, index: number) => {
                  const isSelected = quizSelectedOption === index;
                  const isCorrectAnswer = index === currentSegment.metadata?.correctAnswer;

                  // Determine styling state
                  let buttonClass = "w-full p-3 md:p-4 rounded-xl border-2 text-left text-sm md:text-base font-medium transition-all relative ";

                  if (isSelected) {
                    if (quizIsCorrect) {
                      buttonClass += "bg-green-50 border-green-500 text-green-700 shadow-md";
                    } else {
                      buttonClass += "bg-red-50 border-red-500 text-red-700 shadow-sm";
                    }
                  } else {
                    buttonClass += "bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700";
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleQuizOptionClick(index)}
                      className={buttonClass}
                    >
                      <span className="flex items-center justify-between">
                        {option}
                        {isSelected && (
                          <span>
                            {quizIsCorrect ? <Icons.Check className="w-5 h-5" /> : <Icons.X className="w-5 h-5" />}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {quizIsCorrect === false && (
                <div className="mt-4 text-red-600 text-sm font-medium animate-pulse">
                  Incorrect. Try again!
                </div>
              )}
              {quizIsCorrect === true && (
                <div className="mt-4 text-green-600 text-sm font-medium">
                  Correct! Great job.
                </div>
              )}
            </div>
          )}

          {currentSegment.type === 'question' && (
            <div className="w-full max-w-md mx-auto relative z-10">
              <QuestionSegment
                studyId={study.id}
                segmentId={currentSegment.id}
                userId={userId}
                user={user}
                prompt={currentSegment.metadata?.questionPrompt || 'Have questions about this study?'}
                placeholder={currentSegment.metadata?.questionPlaceholder || 'Ask your question here...'}
                onSkip={handleNext}
              />
            </div>
          )}

          {currentSegment.type === 'cta' && currentSegment.metadata?.ctaType && (
            <div className="w-full max-w-lg mx-auto relative z-10">
              <CTAInterstitial
                cta={{
                  id: currentSegment.id,
                  type: currentSegment.metadata.ctaType,
                  title: currentSegment.content || 'Call To Action',
                  description: undefined,
                  buttonText: currentSegment.metadata.ctaButtonText,
                  skipText: currentSegment.metadata.ctaSkipText,
                  config: currentSegment.metadata.ctaConfig || { type: currentSegment.metadata.ctaType } as CTATypeConfig
                }}
                studyId={study.id}
                segmentId={currentSegment.id}
                orgId={study.orgId}
                user={user || null}
                organization={organization || null}
                onComplete={handleNext}
                onSkip={handleNext}
                onOpenAuthModal={(mode) => openAuthModal(mode)}
              />
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
